/**
 * Pure helpers for deriving approval-progress UI state from a folder's
 * `FolderApprovalProgressDto`.
 *
 * Migrated from `pages/ProjectDetail/sections/Folders/folders.utils.ts` under
 * These helpers are entity-level domain logic
 * (every Folder consumer needs them), not page-local utilities.
 *
 * Test coverage: `lib/__tests__/approval.utils.test.ts` (relocated from
 * `pages/ProjectDetail/sections/Folders/folders.utils.test.ts`).
 */
import type { FolderApprovalProgressSummary } from '../model/types';
import { i18n } from '@/shared/i18n';

const EMPTY_PROGRESS: FolderApprovalProgressSummary = {
  approvedCount: 0,
  totalCount: 0,
  status: 'to_review',
};

export type ApprovalStatus = 'empty' | 'in_progress' | 'complete';

export function getApprovalStatus(
  progress: FolderApprovalProgressSummary | undefined,
): ApprovalStatus {
  const p = progress ?? EMPTY_PROGRESS;
  if (p.totalCount === 0) return 'empty';
  if (p.approvedCount === p.totalCount) return 'complete';
  return 'in_progress';
}

export function getApprovalLabel(progress: FolderApprovalProgressSummary | undefined): string {
  const p = progress ?? EMPTY_PROGRESS;
  if (p.totalCount === 0) return i18n.t('projects:projectFolder.approvalUtils.noAssetsUploaded');
  return i18n.t('projects:projectFolder.approvalUtils.approvedOfTotal', {
    approved: p.approvedCount,
    total: p.totalCount,
  });
}

export function getApprovalClasses(progress: FolderApprovalProgressSummary | undefined): {
  bg: string;
  text: string;
} {
  switch (getApprovalStatus(progress)) {
    case 'empty':
      return { bg: 'bg-muted', text: 'text-muted-foreground' };
    case 'complete':
      return { bg: 'bg-positive', text: 'text-positive-foreground' };
    case 'in_progress':
      return { bg: 'bg-warning', text: 'text-warning-foreground' };
  }
}
